/**
 * Campaign Manifest (spec §34) - the durable plan for a campaign.
 *
 * When the tenant clicks Generate Campaign the system resolves auction truth
 * and the effective brand, freezes both into snapshots, and writes a manifest:
 * the objective, the two snapshots, the creative direction, the source media,
 * and the Creative/Copy/Distribution specs the requested channels resolved to.
 * The manifest is what the jobs render against and what a duplicate or a replay
 * reads. It embeds the snapshots by value so it is self-contained (spec §24,
 * §35): a manifest never has to reach back into mutable brand or auction state.
 */

import { z } from 'zod';
import { CampaignObjectiveSchema, VisualStyleSchema, MarketingChannelSchema, CreativeFormatSchema, FeaturedLotSelectionSchema, ResultsDisclosureSchema } from './catalog.js';
import { BrandKitSnapshotSchema } from './brand-kit.js';
import { AuctionTruthSnapshotSchema } from './auction-truth.js';
import { CreativeSpecSchema, CopyCreativeSpecSchema } from './creative-spec.js';

/** Step 4 of the create flow, as structured config (never a raw prompt). */
export const CreativeDirectionSchema = z
  .object({
    style: VisualStyleSchema,
    /** Freeform tenant direction. Treated as creative direction, fed to the
     *  planner - not passed to a provider verbatim (spec §10, §42). */
    notes: z.string().max(2000).optional(),
    featuredLots: FeaturedLotSelectionSchema.optional(),
    /** Sale Results only: frozen with the manifest so a campaign never
     *  discloses more later than the tenant chose at generation. */
    resultsDisclosure: ResultsDisclosureSchema.optional(),
  })
  .strict();
export type CreativeDirection = z.infer<typeof CreativeDirectionSchema>;

/** A piece of source media the campaign draws on. */
export const CampaignSourceAssetSchema = z
  .object({
    mediaAssetId: z.string().min(1),
    /** How the campaign uses it: a subject photo, a logo, a background, ... */
    role: z.string().optional(),
    lotId: z.string().min(1).optional(),
  })
  .strict();
export type CampaignSourceAsset = z.infer<typeof CampaignSourceAssetSchema>;

/** One channel's slice of the plan: which formats it takes. Attribution is
 *  built per asset at publish time (attribution.ts), not stored here. */
export const DistributionSpecSchema = z
  .object({
    channel: MarketingChannelSchema,
    formats: z.array(CreativeFormatSchema).min(1),
  })
  .strict();
export type DistributionSpec = z.infer<typeof DistributionSpecSchema>;

export const CampaignManifestSchema = z
  .object({
    campaignId: z.string().min(1),
    tenantId: z.string().min(1),
    auctionId: z.string().min(1),
    objective: CampaignObjectiveSchema,
    brandKitSnapshot: BrandKitSnapshotSchema,
    auctionTruth: AuctionTruthSnapshotSchema,
    creativeDirection: CreativeDirectionSchema,
    sourceAssets: z.array(CampaignSourceAssetSchema),
    creativeSpecs: z.array(CreativeSpecSchema),
    copySpecs: z.array(CopyCreativeSpecSchema),
    distributionSpecs: z.array(DistributionSpecSchema),
    createdAt: z.string().datetime({ offset: true }),
  })
  .strict();
export type CampaignManifest = z.infer<typeof CampaignManifestSchema>;
